import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoadingSpinner } from './LoadingSpinner';
import { DisclaimerGate } from '../auth/DisclaimerGate';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  allowAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  allowAdmin = false,
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();


  // Only block the whole page with a full-screen spinner on the FIRST load,
  // when we have no user to render yet. A background re-validation (e.g. after
  // returning from a backgrounded mobile tab, or a network online blip) keeps
  // `user` populated from the last valid session, so we render the page
  // normally and let validation finish silently instead of unmounting the
  // whole route — which is what made a quick app-switch look like a slow full
  // reload. If that background validation reveals a genuinely expired session,
  // rehydrateAuth clears `user`/`isAuthenticated` and the redirect below fires.
  if (loading && !user) {
    return <LoadingSpinner size="lg" className="h-screen" />;
  }

  if (!isAuthenticated) {
    const nextPath = location.pathname + (location.search || '');
    const loginTo = nextPath ? `/onecare/login?next=${encodeURIComponent(nextPath)}` : '/onecare/login';
    return <Navigate to={loginTo} state={{ from: location }} replace />;
  }

  if (requiredRoles.length > 0 || allowAdmin) {
    if (!user) {
      const nextPath = location.pathname + (location.search || '');
      const loginTo = nextPath ? `/onecare/login?next=${encodeURIComponent(nextPath)}` : '/onecare/login';
      return <Navigate to={loginTo} state={{ from: location }} replace />;
    }

    // Check if user is any type of admin - PHASE 6: Updated
    const isAdmin = user.is_superuser ||
                    user.is_organization_admin ||
                    user.is_domain_admin;

    // If allowAdmin is true and user is admin, grant access
    if (allowAdmin && isAdmin) {
      return <DisclaimerGate>{children}</DisclaimerGate>;
    }

    // Check if user is PE_ADV
    const isPEAdv = user.role_specialty?.name === 'Patient Experience Advanced';
    if (isPEAdv && (requiredRoles.length === 0 || requiredRoles.includes('PE_ADV'))) {
      return <DisclaimerGate>{children}</DisclaimerGate>;
    }

    // Check regular role requirements
    if (requiredRoles.length > 0) {
      const hasRequiredRole = isAdmin || requiredRoles.some(role =>
        user.role_display === role ||
        user.role_specialty?.name === role ||
        user.role_specialty?.role_group_name === role ||
        role === 'PE_ADV' // Allow PE_ADV specifically
      );

      if (!hasRequiredRole) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Access Denied
              </h2>
              <p className="text-gray-900">
                You don't have permission to access this page.
              </p>
            </div>
          </div>
        );
      }
    }
  }

  return <DisclaimerGate>{children}</DisclaimerGate>;
};

export default ProtectedRoute;